import { useState } from "react";
import { ChevronDown, Lock, Plus } from "lucide-react";
import { EntryCard } from "./EntryCard";
import { EntryForm } from "./EntryForm";
import { FieldInput } from "./DiaryInputs";
import { fmtDay, getDay, isBlank, plural, setDay } from "./diaryUtils";

function DayNotes({ fields, notes, onChange, testId }) {
  const [open, setOpen] = useState(fields.some((f) => !isBlank(notes[f.code])));
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className="text-base font-semibold text-sky-800 underline" data-testid={`${testId}-open`}>Add symptoms, comments or an achievement for this day</button>;
  }
  return (
    <div className="space-y-4" data-testid={testId}>
      {fields.map((f) => (
        <div key={f.code}>
          <div className="mb-2 text-base font-semibold text-zinc-900">{f.prompt}</div>
          <FieldInput field={f} value={notes[f.code]} onChange={(v) => onChange({ ...notes, [f.code]: v })} testId={`${testId}-${f.code}`} />
        </div>
      ))}
    </div>
  );
}

/** One diary day: entries, add/edit form and the day's optional notes. */
export function DayCard({ day, isToday, defaultOpen, template, diary, onDiary, readOnly }) {
  const [open, setOpen] = useState(defaultOpen ?? isToday);
  const [editing, setEditing] = useState(null);
  const data = getDay(diary, day.n);
  const tid = `diary-day-${day.n}`;
  const update = (patch) => onDiary(setDay(diary, day.n, { ...data, ...patch }));
  const saveEntry = (e) => {
    const exists = data.entries.some((x) => x.id === e.id);
    update({ entries: exists ? data.entries.map((x) => (x.id === e.id ? e : x)) : [...data.entries, e] });
    setEditing(null);
  };
  const full = data.entries.length >= template.max_entries_per_day;
  if (!day.open) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-dashed border-zinc-300 bg-white/60 px-4 py-3 text-zinc-500" data-testid={tid}>
        <Lock className="h-4 w-4" /> <span className="text-base">Day {day.n} · opens {fmtDay(day.date, { weekday: "long", day: "numeric", month: "short" })}</span>
      </div>
    );
  }
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white" data-testid={tid}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`${tid}-toggle`}
              className="flex w-full items-center gap-3 px-4 py-4 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 rounded-2xl">
        <div className="flex-1">
          <div className="font-display text-xl font-bold text-zinc-950">Day {day.n}{isToday && <span className="ml-2 rounded-full bg-[var(--pr-brand,#18181b)] px-2.5 py-0.5 align-middle text-xs font-semibold text-[var(--pr-brand-fg,#fff)]">Today</span>}</div>
          <div className="text-base text-zinc-600">{fmtDay(day.date)} · {data.entries.length ? plural(data.entries.length, template.entry_label) : "nothing logged yet"}</div>
        </div>
        <ChevronDown className={`h-6 w-6 text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-zinc-100 px-4 pb-5 pt-4">
          {data.entries.length > 0 && (
            <ul className="space-y-3">
              {data.entries.map((e, i) => (editing === e.id ? (
                <li key={e.id}><EntryForm fields={template.entry_fields} entry={e} label={template.entry_label} onSave={saveEntry} onCancel={() => setEditing(null)} testId={`${tid}-edit-form`} /></li>
              ) : (
                <EntryCard key={e.id} entry={e} entryFields={template.entry_fields} followupFields={template.followup_fields} followupOpen={day.followup_open}
                           readOnly={readOnly} testId={`${tid}-entry-${i}`} onEdit={() => setEditing(e.id)}
                           onDelete={() => window.confirm("Delete this entry?") && update({ entries: data.entries.filter((x) => x.id !== e.id) })}
                           onChange={(ne) => update({ entries: data.entries.map((x) => (x.id === ne.id ? ne : x)) })} />
              )))}
            </ul>
          )}
          {!readOnly && editing === "new" && <EntryForm fields={template.entry_fields} label={template.entry_label} onSave={saveEntry} onCancel={() => setEditing(null)} testId={`${tid}-new-form`} />}
          {!readOnly && editing !== "new" && !full && (
            <button type="button" onClick={() => setEditing("new")} data-testid={`${tid}-add`}
                    className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-400 text-lg font-semibold text-zinc-800 hover:border-zinc-700">
              <Plus className="h-5 w-5" /> Add an {template.entry_label}
            </button>
          )}
          {template.day_fields.length > 0 && !readOnly && <DayNotes fields={template.day_fields} notes={data.notes || {}} onChange={(notes) => update({ notes })} testId={`${tid}-notes`} />}
        </div>
      )}
    </section>
  );
}
